import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scanner } from '@yudiel/react-qr-scanner';
import { getBackendUrl } from '../utils/api';
import { toast } from 'sonner';

const API = getBackendUrl();

const AdminCheckIn = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [checkInStatus, setCheckInStatus] = useState(null);
  const [ticketInput, setTicketInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [filter, setFilter] = useState('all'); // all, checked_in, not_checked_in
  const [scannerEnabled, setScannerEnabled] = useState(false);
  const [lastScanned, setLastScanned] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchEvents();
  }, [token, navigate]);

  const fetchEvents = async () => {
    try {
      // Use the check-in specific endpoint (accessible to marshals)
      const response = await fetch(`${API}/admin/booking/check-in/events`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.events) {
        setEvents(data.events);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchCheckInStatus = async (eventId) => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/booking/events/${eventId}/check-in-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCheckInStatus(data);
      }
    } catch (error) {
      console.error('Error fetching check-in status:', error);
      toast.error('Failed to load check-in status');
    } finally {
      setLoading(false);
    }
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    setCheckInStatus(null);
    setVerifyResult(null);
    setTicketInput('');
    setScannerEnabled(false);
    setLastScanned(null);
    fetchCheckInStatus(event.id);
  };

  const handleQRScan = async (result) => {
    if (!result || !result[0]?.rawValue) return;
    
    const scannedText = result[0].rawValue.trim().toUpperCase();
    
    // Prevent duplicate scans within 2 seconds
    if (lastScanned === scannedText) return;
    setLastScanned(scannedText);
    
    // Clear after 2 seconds to allow re-scanning same ticket
    setTimeout(() => setLastScanned(null), 2000);
    
    // Process the scanned ticket
    setTicketInput(scannedText);
    await processTicket(scannedText);
  };

  const processTicket = async (ticketNumber) => {
    if (!ticketNumber.trim()) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${API}/admin/booking/check-in/verify?ticket_number=${encodeURIComponent(ticketNumber.trim())}&event_id=${selectedEvent?.id || ''}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await response.json();
      
      if (response.ok && data.success) {
        setVerifyResult(data);
        if (data.already_checked_in) {
          toast.info(`Already checked in: ${data.customer_name}`);
        } else {
          toast.success(`✓ Checked in: ${data.customer_name}`);
        }
        // Refresh status
        if (selectedEvent) {
          fetchCheckInStatus(selectedEvent.id);
        }
      } else {
        toast.error(data.detail || 'Ticket not found');
        setVerifyResult({ error: data.detail || 'Ticket not found' });
      }
    } catch (error) {
      console.error('Error verifying ticket:', error);
      toast.error('Failed to verify ticket');
    } finally {
      setLoading(false);
      setTicketInput('');
    }
  };

  const handleVerifyTicket = async (e) => {
    e.preventDefault();
    await processTicket(ticketInput);
  };

  const handleCheckIn = async (bookingId) => {
    try {
      const response = await fetch(`${API}/admin/booking/bookings/${bookingId}/check-in`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        fetchCheckInStatus(selectedEvent.id);
      } else {
        toast.error(data.detail || 'Failed to check in');
      }
    } catch (error) {
      console.error('Error checking in:', error);
      toast.error('Failed to check in');
    }
  };

  const handleUndoCheckIn = async (bookingId) => {
    try {
      const response = await fetch(`${API}/admin/booking/bookings/${bookingId}/undo-check-in`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Check-in undone');
        fetchCheckInStatus(selectedEvent.id);
      } else {
        toast.error(data.detail || 'Failed to undo check-in');
      }
    } catch (error) {
      console.error('Error undoing check-in:', error);
      toast.error('Failed to undo check-in');
    }
  };

  const handleExportCSV = () => {
    if (!selectedEvent) return;
    window.open(`${API}/admin/booking/events/${selectedEvent.id}/attendees/export?token=${token}`, '_blank');
  };

  const filteredAttendees = checkInStatus?.attendees?.filter(a => {
    if (filter === 'checked_in') return a.checked_in;
    if (filter === 'not_checked_in') return !a.checked_in;
    return true;
  }) || [];

  if (!token) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#2d3a28] text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-gray-300 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-[#c5bfa9]">Event Check-In</h1>
          </div>
          
          {selectedEvent && (
            <a
              href={`${API}/admin/booking/events/${selectedEvent.id}/attendees/export`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </a>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        {/* Event Selection */}
        {!selectedEvent ? (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Event</h2>
            {events.length === 0 ? (
              <p className="text-gray-500">No active events found</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {events.map(event => (
                  <button
                    key={event.id}
                    onClick={() => handleEventSelect(event)}
                    className="p-4 border rounded-lg text-left hover:border-[#4a5d42] hover:bg-[#f8faf7] transition-colors"
                  >
                    <h3 className="font-medium text-gray-900">{event.name}</h3>
                    <p className="text-sm text-gray-500">{event.date}</p>
                    <p className="text-sm text-gray-500">{event.location}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Event Header */}
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="text-sm text-gray-500 hover:text-gray-700 mb-1"
                  >
                    ← Change event
                  </button>
                  <h2 className="text-xl font-bold text-gray-900">{selectedEvent.name}</h2>
                  <p className="text-gray-500">{selectedEvent.date} • {selectedEvent.location}</p>
                </div>
                
                {checkInStatus?.summary && (
                  <div className="text-right">
                    <div className="text-3xl font-bold text-[#4a5d42]">
                      {checkInStatus.summary.checked_in}/{checkInStatus.summary.total}
                    </div>
                    <div className="text-sm text-gray-500">
                      {checkInStatus.summary.percentage}% checked in
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Check-In */}
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Quick Check-In</h3>
                <button
                  onClick={() => setScannerEnabled(!scannerEnabled)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    scannerEnabled 
                      ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                      : 'bg-[#4a5d42] text-white hover:bg-[#3d4a35]'
                  }`}
                  data-testid="toggle-scanner-btn"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                  {scannerEnabled ? 'Close Scanner' : 'Scan QR Code'}
                </button>
              </div>
              
              {/* QR Scanner */}
              {scannerEnabled && (
                <div className="mb-4">
                  <div className="bg-black rounded-lg overflow-hidden max-w-md mx-auto" style={{ aspectRatio: '1' }}>
                    <Scanner
                      onScan={handleQRScan}
                      onError={(error) => {
                        console.error('Scanner error:', error);
                        toast.error('Camera error - please allow camera access');
                      }}
                      constraints={{
                        facingMode: 'environment'
                      }}
                      styles={{
                        container: { width: '100%', height: '100%' },
                        video: { width: '100%', height: '100%', objectFit: 'cover' }
                      }}
                    />
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-2">
                    Point camera at QR code on ticket
                  </p>
                </div>
              )}
              
              {/* Manual Entry */}
              <form onSubmit={handleVerifyTicket} className="flex gap-2">
                <input
                  type="text"
                  value={ticketInput}
                  onChange={(e) => setTicketInput(e.target.value.toUpperCase())}
                  placeholder="Enter ticket number (e.g., FDE-202603-0001)"
                  className="flex-1 px-4 py-3 border rounded-lg text-lg font-mono text-gray-900"
                  data-testid="ticket-input"
                />
                <button
                  type="submit"
                  disabled={loading || !ticketInput.trim()}
                  className="px-6 py-3 bg-[#4a5d42] text-white rounded-lg hover:bg-[#3d4a35] disabled:bg-gray-300 transition-colors font-medium"
                  data-testid="verify-ticket-btn"
                >
                  {loading ? 'Checking...' : 'Check In'}
                </button>
              </form>
              
              {/* Verify Result */}
              {verifyResult && !verifyResult.error && (
                <div className={`mt-4 p-4 rounded-lg ${verifyResult.already_checked_in ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                  <div className="flex items-center gap-3">
                    {verifyResult.already_checked_in ? (
                      <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    ) : (
                      <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    <div>
                      <p className="font-bold text-lg text-gray-900">{verifyResult.customer_name}</p>
                      <p className="text-sm text-gray-600">Team: {verifyResult.team_name}</p>
                      <p className="text-xs text-gray-500">{verifyResult.ticket_number}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {verifyResult?.error && (
                <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-red-700">{verifyResult.error}</p>
                </div>
              )}
            </div>

            {/* Team Stats */}
            {checkInStatus?.teams_status && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {Object.entries(checkInStatus.teams_status).map(([teamName, stats]) => (
                  <div key={teamName} className="bg-white rounded-lg shadow p-4">
                    <h4 className="font-medium text-gray-900 mb-2">{teamName}</h4>
                    <div className="flex items-end gap-2">
                      <span className="text-2xl font-bold text-[#4a5d42]">{stats.checked_in}</span>
                      <span className="text-gray-400">/</span>
                      <span className="text-lg text-gray-600">{stats.total}</span>
                    </div>
                    <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#4a5d42] transition-all"
                        style={{ width: `${stats.total > 0 ? (stats.checked_in / stats.total * 100) : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Filter */}
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-lg transition-colors ${filter === 'all' ? 'bg-[#4a5d42] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  All ({checkInStatus?.summary?.total || 0})
                </button>
                <button
                  onClick={() => setFilter('checked_in')}
                  className={`px-4 py-2 rounded-lg transition-colors ${filter === 'checked_in' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Checked In ({checkInStatus?.summary?.checked_in || 0})
                </button>
                <button
                  onClick={() => setFilter('not_checked_in')}
                  className={`px-4 py-2 rounded-lg transition-colors ${filter === 'not_checked_in' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Not Arrived ({checkInStatus?.summary?.not_checked_in || 0})
                </button>
              </div>
            </div>

            {/* Attendee List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-In Time</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredAttendees.map(attendee => (
                      <tr key={attendee.booking_id} className={attendee.checked_in ? 'bg-green-50' : ''}>
                        <td className="px-4 py-3">
                          {attendee.checked_in ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Arrived
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Waiting
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-gray-900">{attendee.ticket_number}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{attendee.customer_name}</div>
                          <div className="text-xs text-gray-500">{attendee.customer_email}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{attendee.team_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {attendee.checked_in_at ? new Date(attendee.checked_in_at).toLocaleTimeString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {attendee.checked_in ? (
                            <button
                              onClick={() => handleUndoCheckIn(attendee.booking_id)}
                              className="text-sm text-gray-500 hover:text-gray-700"
                            >
                              Undo
                            </button>
                          ) : (
                            <button
                              onClick={() => handleCheckIn(attendee.booking_id)}
                              className="px-3 py-1 bg-[#4a5d42] text-white text-sm rounded hover:bg-[#3d4a35] transition-colors"
                            >
                              Check In
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    
                    {filteredAttendees.length === 0 && (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                          No attendees found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminCheckIn;
